// File: src/components/ChatApp.tsx

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import classes from "./ChatApp.module.css";
import { List, Select, Input, Button, Layout, Modal } from "antd";
import api from "../../utils/axios";
import { userRoles } from "../../types/enums";
import { getUserIdFromToken, getUserRoleFromToken } from "../../utils/Auth";
import { fetchAllUsers } from "../../lib/UserApi";
import DefaultInput from "../../components/form/DefaultInput";
import useLang from "../../hooks/useLang";
import { ReactComponent as SearchIcon } from "../../assets/search-find.svg";
import { ReactComponent as DeleteIcon } from "../../assets/deleteIcon.svg";
import { ReactComponent as AddIcon } from "../../assets/add-plus.svg";
import MultiModalSelector from "../../components/form/MultiModalSelector";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";

const { Sider, Content } = Layout;
const { Option } = Select;
const { TextArea } = Input;

interface UserDto {
	id: number;
	firstName?: string;
	lastName?: string;
	email?: string;
	username?: string;
	phone?: string;
	address?: string;
	userRoleId: number;
	userRoleDescription?: string;
	registeredDate: string;
	newMessage: boolean;
}

interface MessagePayload {
	subjectFrom: number;
	subjectTo: number[];
	title: string;
	message: string;
}
type MessagesSelect = {
	id: number;
	title: string;
	subjectFrom: number;
	senderName: string;
	senderEmail: string;
	subjectTo: number;
	receiverName: string;
	receiverEmail: string;
	message: string;
	registeredDate: Date;
	openedDate: string;
	opened: boolean;
};

type User = {
	id: number;
	user: string;
	userRoleId: number;
};

interface ChatAppProps {}

const ChatApp: React.FC<ChatAppProps> = () => {
	const receivedNewMessages = useSelector(
		(state: any) => state.message.receivedNewMessages
	);
	const location = useLocation();
	const userId = location.state?.userId;
	const [chatters, setChatters] = useState<UserDto[]>([]);
	const [users, setUsers] = useState<UserDto[]>([]);
	const [adminUser, setAdminUser] = useState<UserDto | null>(null);
	const [messages, setMessages] = useState<MessagesSelect[]>([]);
	const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
	const [newMessage, setNewMessage] = useState<string>("");
	const [isAdmin, setIsAdmin] = useState<boolean>(false);
	const [open, setOpen] = useState(false);
	const [title, setTitle] = useState("");
	const [message, setMessage] = useState("");
	const [userSearchInput, setUserSearchInput] = useState<
		string | undefined | null
	>("");
	const [filteredUserMessages, setFilteredUserMessages] = useState<UserDto[]>(
		[]
	);
	const [openSendMessageModal, setOpenSendMessageModal] =
		useState<boolean>(false);

	const [userOptions, setUserOptions] = useState<User[]>([]);
	const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
	const [messageIds, setMessageIds] = useState<any>([]);
	const [clientUserOptions, setClientUserOptions] = useState<User[]>([]);
	const messagesEndRef = useRef<HTMLDivElement>(null);

	const getUsersData = async () => {
		return await fetchAllUsers();
	};

	const filterAndMapUsers = (users: any) => {
		return users.map((item: any) => ({
			id: item.id,
			user: `${item.firstName} ${item.lastName} - ${item.userRoleDescription}`,
			userRoleId: item.userRoleId,
		}));
	};

	const getAllUsers = useCallback(async () => {
		const users = await getUsersData();
		if (users) {
			const userOptions = filterAndMapUsers(users);
			setUserOptions(userOptions);
			const options = userOptions.filter(
				(elem: User) => elem.userRoleId === userRoles.ADMIN
			);
			setClientUserOptions(options);
		}
	}, []);

	useEffect(() => {
		const userRoleId = getUserRoleFromToken();
		const isAdmin = userRoleId === userRoles.ADMIN;
		(async () => await fetchUsers(isAdmin))();
		setIsAdmin(isAdmin);
		getAllUsers();
		handleUserChange(userId);
	}, []);

	useEffect(() => {
		if (receivedNewMessages && selectedUserId !== null) {
			fetchMessages(selectedUserId);
		}
	}, [receivedNewMessages, selectedUserId]);

	const sortUsersByLastMessage = useCallback(
		(usersToSort: UserDto[], dates: Record<number, string>) => {
			return [...usersToSort].sort((a, b) => {
				const dateA = dates[a.id] ? new Date(dates[a.id]).getTime() : 0;
				const dateB = dates[b.id] ? new Date(dates[b.id]).getTime() : 0;

				return dateB - dateA;
			});
		},
		[]
	);

	const fetchUsers = async (isAdmin: boolean) => {
		try {
			const response = await api.get<UserDto[]>("user");
			const allUsers = response.data;
			setUsers(allUsers);

			const adminUsers = allUsers.filter(
				(user) => user.userRoleId === userRoles.ADMIN
			);
			if (adminUsers.length > 0) {
				setAdminUser(adminUsers[0]);
			}

			const targetUsers = isAdmin ? allUsers : adminUsers;
			const messageDates = await Promise.all(
				targetUsers.map(async (user) => {
					try {
						const response = await api.get<MessagesSelect[]>(
							"message/chat-message-select",
							{
								params: { selectedUserId: user.id },
							}
						);
						const messages = response.data;
						if (messages.length > 0) {
							const sortedMessages = messages.sort(
								(a, b) =>
									new Date(b.registeredDate).getTime() -
									new Date(a.registeredDate).getTime()
							);
							return {
								userId: user.id,
								date: sortedMessages[0].registeredDate,
							};
						}
						return { userId: user.id, date: "" };
					} catch (error) {
						console.error(
							`Error fetching messages for user ${user.id}:`,
							error
						);
						return { userId: user.id, date: "" };
					}
				})
			);

			const datesMap = messageDates.reduce(
				(acc: any, { userId, date }) => {
					acc[userId] = date;
					return acc;
				},
				{} as Record<number, string>
			);

			const initialChatters = isAdmin
				? targetUsers
				: adminUsers.slice(0, 1);
			const sortedChatters = sortUsersByLastMessage(
				initialChatters,
				datesMap
			);
			setChatters(sortedChatters);
			setFilteredUserMessages(sortedChatters);
			setLastMessageDates(datesMap);
		} catch (error) {
			console.error("Error fetching users:", error);
		}
	};

	const [lastMessageDates, setLastMessageDates] = useState<
		Record<number, string>
	>({});

	const fetchMessages = async (userId: number) => {
		try {
			const response = await api.get<MessagesSelect[]>(
				"message/chat-message-select",
				{
					params: { selectedUserId: userId },
				}
			);
			const sortedMessages = response.data.sort(
				(a, b) =>
					new Date(b.registeredDate).getTime() -
					new Date(a.registeredDate).getTime()
			);
			setMessages(sortedMessages);
		} catch (error) {
			console.error("Error fetching messages:", error);
		}
	};
	const [selectedUser, setSelectedUser] = useState<UserDto | null>(null);

	const handleUserChange = (userId: number) => {
		setSelectedUserId(userId);
		const selectedUser = users.find((user) => user.id === userId) || null;
		setSelectedUser(selectedUser);
		fetchMessages(userId);
		setLastMessageDates((prevDates) => {
			const updatedDates = {
				...prevDates,
				[userId]: new Date().toISOString(),
			};
			setChatters((prevChatters) =>
				sortUsersByLastMessage(prevChatters, updatedDates)
			);
			setFilteredUserMessages((prevFiltered) =>
				sortUsersByLastMessage(prevFiltered, updatedDates)
			);
			return updatedDates;
		});
		if (messagesEndRef.current) {
			messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
		}
	};

	const handleSendMessage = async () => {
		if (selectedUserId !== null && newMessage) {
			try {
				if (isAdmin) {
					const payload: MessagePayload = {
						subjectFrom: 0,
						subjectTo: [selectedUserId],
						title: "Admin message",
						message: newMessage,
					};
					await api.post("message/message-insert", payload, {
						headers: { "Content-Type": "application/json" },
					});
				} else {
					const sendingUser: UserDto | undefined = users.find(
						(e) => e.id === getUserIdFromToken()
					);
					const sendingUserName: string | undefined =
						sendingUser?.username;
					const payload: MessagePayload = {
						subjectFrom: 0,
						subjectTo: users
							.filter(
								(user) => user.userRoleId === userRoles.ADMIN
							)
							.map((e: UserDto) => e.id),
						title: sendingUserName
							? sendingUserName
							: "User message",
						message: newMessage,
					};
					await api.post("message/message-insert", payload, {
						headers: { "Content-Type": "application/json" },
					});
				}
				setNewMessage("");
				fetchMessages(selectedUserId);
			} catch (error) {
				console.error("Error sending message:", error);
			}
		}
	};

	const handleSendAllMessage = async () => {
		const payload: MessagePayload = {
			subjectFrom: 0,
			subjectTo: users.map((e) => e.id),
			title: "Admin message",
			message: message,
		};
		await api.post("message/message-insert", payload, {
			headers: { "Content-Type": "application/json" },
		});
	};

	const { strings } = useLang("messages");

	const handleUserSearchChange = (e: any) => {
		const input = e.target.value;
		setUserSearchInput(e.target.value);
		const filteredChatters = chatters.filter(
			(item: any) =>
				item.firstName
					.toString()
					.toLowerCase()
					.includes(input.toString().toLowerCase()) ||
				item.lastName
					.toString()
					.toLowerCase()
					.includes(input.toString().toLowerCase()) ||
				item.username
					.toString()
					.toLowerCase()
					.includes(input.toString().toLowerCase())
		);
		setFilteredUserMessages(filteredChatters);
	};

	const formatTime = (dateString: string) => {
		const date = new Date(dateString);
		const hours = date.getHours().toString().padStart(2, "0");
		const minutes = date.getMinutes().toString().padStart(2, "0");
		return `${hours}:${minutes}`;
	};

	const onMessageReceiversSelected = (rows: any[]) => {
		setSelectedUsers(rows);
	};
	const onMssageReceiverDeleted = () => {
		setSelectedUsers([]);
	};
	const cleanMessageState = () => {
		setTitle("");
		setMessage("");
		setSelectedUsers([]);
	};

	const insertMessage = async () => {
		try {
			if (
				(selectedUsers?.length === 0 && isAdmin) ||
				!title ||
				!message
			) {
				toast.error("Ju lutem plotësoni të gjitha fushat.");
				return;
			}

			const data = {
				subjectTo: isAdmin
					? selectedUsers.map((elem) => elem.id)
					: clientUserOptions.map((elem: any) => elem.id),
				title: title,
				message: message,
			};
			const response = await api.post("Message/message-insert", data);
			if (response.status === 200) {
				cleanMessageState();
				setOpenSendMessageModal(false);
				toast.success("Mesazhi u dergu me sukses");
			}
		} catch (error: any) {
			console.error("An error occurred: " + error.response.data);
		}
	};
	console.log(userId, "userrrIDDD");

	return (
		<>
			<Modal
				open={open}
				title={strings?.newMessage}
				centered
				onCancel={() => {
					setOpen(false);
					setTitle("");
					setMessage("");
				}}
				width={650}
				footer={(_, { OkBtn, CancelBtn }) => (
					<>
						<div className="flex justify-end items-end w-full h-16">
							<Button
								type="primary"
								size="large"
								className="bg-blue-700"
								onClick={handleSendAllMessage}
							>
								Send Message
							</Button>
						</div>
					</>
				)}
			>
				<div className="flex flex-col gap-4">
					<DefaultInput
						className={classes.registerInput}
						label={strings?.subjectLabel}
						placeholder={strings?.subjectPlaceholder}
						value={title}
						onChange={(e) => setTitle(e.target.value)}
					/>
					<div>
						<p>{strings?.messageLabel}</p>
						<TextArea
							rows={6}
							value={message}
							onChange={(e) => setMessage(e.target.value)}
							placeholder={strings?.messagePlaceholder}
						/>
					</div>
				</div>
			</Modal>

			<div className={classes.mainContainer}>
				<div className={classes.heroContainer}>
					<div className={classes.headContainer}>
						<h1 className={classes.title}>{strings?.mainTitle}</h1>
						<div className={classes.buttons}>
							<Button
								className={classes.button}
								onClick={() => setOpenSendMessageModal(true)}
							>
								<AddIcon />
								{strings?.createNewMessageButton}
							</Button>
						</div>
					</div>
					<div className={classes.container}>
						<div className={classes.firstContainer}>
							<div className="flex flex-col gap-2 p-6 pb-0">
								<DefaultInput
									placeholder={strings?.searchLabel}
									prefix={<SearchIcon />}
									className="inputSettlement"
									value={userSearchInput}
									onChange={handleUserSearchChange}
									allowClear={true}
								/>
								{isAdmin ? (
									<Button
										type="default"
										onClick={(e) => setOpen(true)}
										className={classes.firstButton}
									>
										{strings?.sendAllUsers}
									</Button>
								) : (
									<></>
								)}
							</div>
							<List
								bordered
								dataSource={filteredUserMessages}
								className={classes.usersList}
								renderItem={(item: UserDto) => (
									<List.Item
										onClick={() =>
											handleUserChange(item.id)
										}
										style={{
											cursor: "pointer",
											backgroundColor:
												item.id === selectedUserId
													? "#FAFAFA"
													: item.newMessage === false
													? "#4f16c2"
													: "#fff",
										}}
										className={classes.usernameDiv}
									>
										<div className={classes.userInfos}>
											<span className={classes.username}>
												{item.username}
											</span>
											<span
												className={
													classes.userRoleDescription
												}
											>
												{item.userRoleDescription}
											</span>
										</div>
										<span
											className={classes.registrationDate}
										>
											{lastMessageDates[item.id]
												? formatTime(
														lastMessageDates[
															item.id
														]
												  )
												: ""}
										</span>
									</List.Item>
								)}
							/>
						</div>
						<div className={classes.secondContainer}>
							{selectedUser && (
								<div className={classes.selectedUserInfoMain}>
									<div className={classes.selectedUserInfo}>
										<p className={classes.username}>
											{selectedUser.firstName}
										</p>
										<p
											className={
												classes.userRoleDescription
											}
										>
											{selectedUser.userRoleDescription}
										</p>
									</div>
									<div className="flex  items-center justify-center gap-1 ">
										<DeleteIcon
											color="#8E8E8E"
											className="text-[#8E8E8E]"
										/>
										<p className="font-inter font-normal text-sm leading-5 text-gray-500">
											{strings?.delete}
										</p>
									</div>
								</div>
							)}
							<List
								bordered={false}
								dataSource={messages}
								className={classes.messagesList}
								renderItem={(message: MessagesSelect) => (
									<div
										className={
											message.subjectTo === selectedUserId
												? classes.userMessageContainer
												: classes.receivedMessageContainer
										}
									>
										<List.Item
											className={classes.messageItem}
										>
											<div
												className={
													message.subjectTo ===
													selectedUserId
														? classes.userMessageBubble
														: classes.receivedMessageBubble
												}
											>
												{/* <p
													className={
														classes.messageSenderHeader
													}
												>{`Mesazhi nga: ${message.senderName}`}</p> */}
												<p
													className={
														classes.messageTitle
													}
												>
													{message.title}
												</p>
												<p>{message.message}</p>
											</div>
										</List.Item>
									</div>
								)}
							/>
							<div ref={messagesEndRef} />
						</div>
					</div>
				</div>
			</div>

			<Modal
				open={openSendMessageModal}
				title={strings?.newMessageModalTitle}
				centered
				onCancel={() => {
					setOpenSendMessageModal(false);
					setTitle("");
					setMessage("");
					setSelectedUsers([]);
				}}
				width={650}
				footer={(_, { OkBtn, CancelBtn }) => (
					<>
						<div className="flex justify-end items-end w-full h-16">
							<Button
								type="primary"
								size="large"
								className="bg-blue-700"
								onClick={insertMessage}
							>
								{strings?.sendMessageButton}
							</Button>
						</div>
					</>
				)}
			>
				<div className="flex flex-col gap-4">
					{isAdmin === true ? (
						<MultiModalSelector
							primaryKey="value"
							tableData={
								isAdmin ? userOptions : clientUserOptions
							}
							buttonLabel={strings?.receiverLabel}
							buttonDesc={strings?.receiverPlaceholder}
							buttonDescAll="All"
							onRowsSelected={onMessageReceiversSelected}
							onRowsCleared={onMssageReceiverDeleted}
							value={selectedUsers}
							selectorClassName={classes.registerInput}
							inputName="Select receiver"
							disabled={false}
						/>
					) : (
						<></>
					)}

					<DefaultInput
						className={classes.registerInput}
						label={strings?.subjectLabel}
						placeholder={strings?.subjectPlaceholder}
						value={title}
						onChange={(e) => setTitle(e.target.value)}
					/>
					<div>
						<p>{strings?.messageLabel}</p>
						<TextArea
							rows={6}
							value={message}
							onChange={(e) => setMessage(e.target.value)}
							placeholder={strings?.messagePlaceholder}
						/>
					</div>
				</div>
			</Modal>
		</>
	);
};

export default ChatApp;
